import { utcIsoToWallTime, wallTimeToUtcIso } from './orders';

/**
 * An order taken on paper while the app could not reach the server, entered once it can (ADR-040). The time on the
 * paper is the location's wall-clock time, today or, when that is later than now, before midnight; the server keeps it
 * apart from its own times and refuses one before the shift opened or on another business day.
 */
export interface PaperRecoveryInput {
  /** "14:05", the location's time today. */
  time: string;
  /** The paper has no exact time: it is the cashier's estimate. */
  estimated: boolean;
  /** The paper slip's number, when it has one: letters, digits and dashes. */
  slip: string;
}

export interface PaperRecoveryPayload {
  source: 'paper';
  operational_at: string;
  estimated: boolean;
  slip?: string;
}

export const EMPTY_PAPER: PaperRecoveryInput = { time: '', estimated: true, slip: '' };

export function paperRecoveryPayload(
  input: PaperRecoveryInput,
  now: Date,
  timeZone: string,
): { ok: true; value: PaperRecoveryPayload } | { ok: false; error: string } {
  const time = input.time.trim();
  if (!/^\d{2}:\d{2}$/.test(time)) return { ok: false, error: 'Enter the time on the paper.' };
  const today = utcIsoToWallTime(now.toISOString(), timeZone).slice(0, 10);
  let at = wallTimeToUtcIso(`${today}T${time}`, timeZone);
  if (!at) return { ok: false, error: 'Enter the time on the paper.' };
  // a time later than now is from before midnight (a shift and its business day run past it): yesterday's date; the
  // server checks it belongs to this shift and business day
  if (new Date(at).getTime() > now.getTime() + 2 * 60_000) {
    const yesterday = utcIsoToWallTime(
      new Date(now.getTime() - 24 * 60 * 60_000).toISOString(),
      timeZone,
    ).slice(0, 10);
    at = wallTimeToUtcIso(`${yesterday}T${time}`, timeZone);
    if (!at) return { ok: false, error: 'Enter the time on the paper.' };
  }
  const slip = input.slip.trim();
  if (slip && !/^[A-Za-z0-9-]{1,20}$/.test(slip))
    return { ok: false, error: 'The slip number is letters, digits and dashes.' };
  return {
    ok: true,
    value: {
      source: 'paper',
      operational_at: at,
      estimated: input.estimated,
      ...(slip ? { slip } : {}),
    },
  };
}
